import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TuneIcon from '@mui/icons-material/Tune';

import { API_ENDPOINTS } from '../config/api';
import QuestionarioDePerfil, { RESPOSTAS_VAZIAS } from './QuestionarioDePerfil';

/**
 * "Nao tenho planilha nenhuma" — a porta de entrada de quem nao tem sistema.
 *
 * A tela de importacao pedia um arquivo e nao dizia QUAL arquivo. Quem nao tem
 * uma DRE pronta — que e a maioria do publico deste produto — parava aqui.
 *
 * O recorte do arquivo NAO sai mais de um seletor de tipo de empresa. Sai de
 * seis perguntas sobre o que a empresa faz (`QuestionarioDePerfil`), porque
 * "padaria ou fabrica" confundia tamanho com complexidade: existe fabrica de
 * fundo de quintal e padaria com cem filiais, e as duas coisas pedem recortes
 * diferentes do plano pelo mesmo motivo — o que elas FAZEM, nao o que elas sao.
 *
 * Sem responder nada, sai o recorte curto, que ja produz DRE, margem e ponto de
 * equilibrio. Responder so acrescenta linha.
 */

const JANELAS = [3, 6, 12];

function BaixarModelo() {
  const [respostas, setRespostas] = useState(RESPOSTAS_VAZIAS);
  const [meses, setMeses] = useState(3);
  const [aberto, setAberto] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState(null);

  const baixar = async () => {
    setBaixando(true);
    setErro(null);
    try {
      const resposta = await fetch(API_ENDPOINTS.UPLOAD_MODELO, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ respostas, meses })
      });
      if (!resposta.ok) throw new Error('Não foi possível gerar a planilha modelo.');

      // O nome do arquivo vem do servidor: e ele que sabe a janela de meses.
      const disposicao = resposta.headers.get('Content-Disposition') || '';
      const casado = disposicao.match(/filename="?([^";]+)"?/);
      const nome = casado ? casado[1] : 'consultoria-modelo.xlsx';

      const blob = await resposta.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nome;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErro(e.message);
    } finally {
      setBaixando(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Não tem uma planilha pronta?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Baixe a nossa, preencha só o que você controla e suba de volta. O que
              ficar em branco continua em branco — não vira zero.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              select
              label="Meses"
              value={meses}
              onChange={(e) => setMeses(Number(e.target.value))}
              size="small"
              sx={{ minWidth: 130 }}
            >
              {JANELAS.map((n) => (
                <MenuItem key={n} value={n}>{n} meses</MenuItem>
              ))}
            </TextField>

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={baixar}
              disabled={baixando}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {baixando ? 'Gerando…' : 'Baixar planilha modelo'}
            </Button>

            <Button
              variant="text"
              startIcon={<TuneIcon />}
              onClick={() => setAberto((v) => !v)}
            >
              {aberto ? 'Fechar' : 'Ajustar ao meu negócio'}
            </Button>
          </Stack>

          {erro && (
            <Typography variant="caption" color="error">{erro}</Typography>
          )}

          <Collapse in={aberto} unmountOnExit>
            <Box sx={{ pt: 1 }}>
              <QuestionarioDePerfil respostas={respostas} onChange={setRespostas} />
            </Box>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default BaixarModelo;
